import itineraryApi from '@/src/api/rtkQuery/itineraryApi'
import {
    AMEND_INSURANCE,
    DOM_CANCELATION_INSURANCE,
    INTL_CANCELATION_INSURANCE,
    INTL_FLEXIFLY
} from '@/src/constants/itnerary'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import { RootState } from '@/src/redux/rootReducer'
import { useAppSelector } from '@/src/redux/store'

import {
    checkInsuranceEligibility,
    extractFlightInfo,
    extractInsuranceProviders
} from '@/src/utils/analytics/clevertap/itinerary'
import { useMemo } from 'react'
import useClevertapEvent from '../index'

const useClevertapForItinerary = () => {
    const { triggerEvent } = useClevertapEvent()
    const itineraryId = useGetItineraryId()

    const data = useAppSelector((state: RootState) => {
        const cachedData = itineraryApi.endpoints.getItineraryData.select({
            itineraryId
        })(state)
        return cachedData?.status === 'fulfilled' ? cachedData.data : null
    })

    const { flights = [], searchCriteria, meta, insurance, itineraryData } = data || {}
    const { vasAncillaries = [] } = itineraryData || {}
    const { insuranceList = [] } = insurance || {}
    const { paxInfo, departDate, arrivalDate, international, multiCity, multiCitySearchCriterias } =
        searchCriteria || {}
    const { adults, children, infants } = paxInfo || {}

    const { airline, airlineName, flightNumber } = useMemo(() => extractFlightInfo(flights), [flights])
    const Flexifly = checkInsuranceEligibility([INTL_FLEXIFLY, AMEND_INSURANCE], vasAncillaries)
    const EzCancel = checkInsuranceEligibility([DOM_CANCELATION_INSURANCE, INTL_CANCELATION_INSURANCE], vasAncillaries)
    const provider = extractInsuranceProviders(insuranceList)

    // Multicity: collapse the journey to first-sector origin → last-sector destination,
    // using the first/last sector dates and the total sector count.
    const mcSectors = Array.isArray(multiCitySearchCriterias) ? multiCitySearchCriterias : []
    const isMultiCity = !!multiCity && mcSectors.length >= 2
    const firstSector = mcSectors[0]
    const lastSector = mcSectors[mcSectors.length - 1]

    const origin = (isMultiCity ? firstSector?.from : flights[0]?.from) || 'N/A'
    const destination = (isMultiCity ? lastSector?.to : flights[0]?.to) || 'N/A'
    const tripType = isMultiCity ? 'MultiCity' : arrivalDate ? 'RoundTrip' : 'OneWay'
    const routeType = international ? 'international' : 'domestic'
    const departureDate = (isMultiCity ? firstSector?.departDate : departDate)?.split('T')[0] || 'N/A'
    const returnDate = (isMultiCity ? lastSector?.departDate : arrivalDate)?.split('T')[0] || 'N/A'
    const sectorCount = isMultiCity ? mcSectors.length : arrivalDate ? 2 : 1
    const searchId = meta?.sid || 'N/A'
    const dx = Math.floor((new Date(departureDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))

    const triggerItineraryEvent = (name: string, payload: any) => {
        const finalPayload = {
            ...payload,
            itineraryId,
            origin,
            destination,
            airline,
            airlineName,
            flightNumber,
            trip_type: tripType,
            route_type: routeType,
            adult_count: adults,
            child_count: children,
            infant_count: infants,
            departure_date: departureDate,
            return_date: returnDate,
            number_of_sectors: sectorCount,
            search_id: searchId,
            Flexifly,
            EzCancel,
            insurance_provider: provider,
            dx
        }

        triggerEvent(name, finalPayload)
    }

    return {
        triggerItineraryEvent
    }
}

export default useClevertapForItinerary
